class SaPriceRange extends HTMLElement {
  static get preservedMaxCents() {
    return window.__saPriceRangeMaxCents || 0;
  }

  static set preservedMaxCents(value) {
    window.__saPriceRangeMaxCents = value;
  }

  initMaxCents() {
    const currentCents = parseInt(this.dataset.rangeMaxCents, 10) || 0;

    if (currentCents > SaPriceRange.preservedMaxCents) {
      SaPriceRange.preservedMaxCents = currentCents;
    }

    this.maxCents = SaPriceRange.preservedMaxCents || currentCents;
    this.rangeMaxValue = Math.floor(this.maxCents / 100);
    this.maxDisplay = this.formatDollars(this.maxCents / 100);
  }

  formatDollars(dollars) {
    if (!Number.isFinite(dollars)) return '0';
    const rounded = Math.round(dollars * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  }

  applyRangeMaxToInputs() {
    this.rangeMin.max = String(this.rangeMaxValue);
    this.rangeMax.max = String(this.rangeMaxValue);
    this.inputMax.setAttribute('data-max', this.maxDisplay);
    this.inputMin.setAttribute('data-max', this.maxDisplay);

    if (!this.inputMax.value) {
      this.inputMax.placeholder = this.maxDisplay;
    }
  }

  connectedCallback() {
    this.rangeMin = this.querySelector('.sa-price-range__range-min');
    this.rangeMax = this.querySelector('.sa-price-range__range-max');
    this.inputMin = this.querySelector('.sa-price-range__input-min');
    this.inputMax = this.querySelector('.sa-price-range__input-max');
    this.track = this.querySelector('.sa-price-range__track');

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax || !this.track) return;

    this.initMaxCents();
    this.applyRangeMaxToInputs();
    this.hasActiveFilter = !this.inputMin.disabled && !this.inputMax.disabled;

    if (this.hasActiveFilter) {
      this.rangeMin.value = Math.round(this.parseInputValue(this.inputMin.value));
      this.rangeMax.value = Math.round(
        this.inputMax.value === '' ? this.rangeMaxValue : this.parseInputValue(this.inputMax.value),
      );
    } else {
      this.rangeMin.value = 0;
      this.rangeMax.value = this.rangeMaxValue;
    }

    if (parseInt(this.rangeMin.value, 10) >= parseInt(this.rangeMax.value, 10)) {
      this.rangeMax.value = Math.min(this.rangeMaxValue, parseInt(this.rangeMin.value, 10) + 1);
    }

    this.rangeMin.addEventListener('input', () => this.onRangeInput());
    this.rangeMax.addEventListener('input', () => this.onRangeInput());
    this.rangeMin.addEventListener('change', () => this.onRangeChange());
    this.rangeMax.addEventListener('change', () => this.onRangeChange());

    this.inputMin.addEventListener('change', () => this.onInputChange(this.inputMin, this.rangeMin));
    this.inputMax.addEventListener('change', () => this.onInputChange(this.inputMax, this.rangeMax));
    this.inputMin.addEventListener('keydown', this.onKeyDown);
    this.inputMax.addEventListener('keydown', this.onKeyDown);

    this.syncInputPreview();
    this.setMinAndMaxValues();
    this.updateTrack();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  onRangeInput() {
    let minVal = parseInt(this.rangeMin.value, 10);
    let maxVal = parseInt(this.rangeMax.value, 10);

    if (minVal >= maxVal) {
      if (document.activeElement === this.rangeMin) {
        minVal = Math.max(0, maxVal - 1);
        this.rangeMin.value = minVal;
      } else {
        maxVal = Math.min(this.rangeMaxValue, minVal + 1);
        this.rangeMax.value = maxVal;
      }
    }

    this.syncInputPreview();
    this.setMinAndMaxValues();
    this.updateTrack();
  }

  onRangeChange() {
    this.onRangeInput();
    this.updateSubmissionState(true);
  }

  onInputChange(input, range) {
    this.inputMin.disabled = false;
    this.inputMax.disabled = false;
    this.adjustToValidValues(input);
    const numericValue = this.parseInputValue(input.value);
    range.value = Math.min(this.rangeMaxValue, Math.max(0, Math.round(numericValue)));
    this.onRangeInput();
    this.updateSubmissionState(true);
  }

  syncInputPreview() {
    this.inputMin.value = this.rangeMin.value;
    this.inputMax.value =
      parseInt(this.rangeMax.value, 10) === this.rangeMaxValue ? this.maxDisplay : this.rangeMax.value;
  }

  syncInputsFromRange() {
    this.inputMin.value = this.rangeMin.value;
    this.inputMax.value =
      parseInt(this.rangeMax.value, 10) === this.rangeMaxValue ? this.maxDisplay : this.rangeMax.value;
  }

  isPriceFilterActive() {
    const minVal = parseInt(this.rangeMin.value, 10);
    const maxVal = parseInt(this.rangeMax.value, 10);
    return minVal > 0 || maxVal < this.rangeMaxValue;
  }

  resetToDefaultRange() {
    this.inputMin.value = '';
    this.inputMax.value = '';
    // this.inputMin.disabled = true;
    // this.inputMax.disabled = true;
    this.rangeMin.value = 0;
    this.rangeMax.value = this.rangeMaxValue;
    this.applyRangeMaxToInputs();
    this.updateTrack();
  }

  updateSubmissionState(shouldSubmit) {
    const active = this.isPriceFilterActive();

    if (active) {
      this.inputMin.disabled = false;
      this.inputMax.disabled = false;
      this.syncInputsFromRange();
    } else {
      this.resetToDefaultRange();
    }

    if (shouldSubmit) {
      this.inputMax.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  parseInputValue(value) {
    const normalized = String(value).replace(/,/g, '');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  setMinAndMaxValues() {
    const maxBound = this.inputMax.disabled
      ? this.maxDisplay
      : this.inputMax.value || this.maxDisplay;
    const minBound = this.inputMin.disabled ? '0' : this.inputMin.value || '0';

    this.inputMin.setAttribute('data-max', maxBound);
    this.inputMax.setAttribute('data-min', minBound);
  }

  adjustToValidValues(input) {
    const value = this.parseInputValue(input.value);
    const min = this.parseInputValue(input.getAttribute('data-min') || '0');
    const max = this.parseInputValue(input.getAttribute('data-max') || this.maxDisplay);

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  updateTrack() {
    if (!this.track) return;
    const minPct = (parseInt(this.rangeMin.value, 10) / this.rangeMaxValue) * 100;
    const maxPct = (parseInt(this.rangeMax.value, 10) / this.rangeMaxValue) * 100;
    this.track.style.setProperty('--range-min', `${minPct}%`);
    this.track.style.setProperty('--range-max', `${maxPct}%`);
  }
}

if (!customElements.get('sa-price-range')) {
  customElements.define('sa-price-range', SaPriceRange);
}
